<template>
    <div
        style="max-width: 600px; margin: auto; text-align: left;"
        :class="field.options?.inline ? 'date-field-inline' : 'date-field-stack'"
    >
        <FieldHeader
            v-if="showHeader"
            :class="field.options?.inline ? 'q-mb-none q-mr-md date-field-header-inline' : 'q-mb-xs'"
            :field="field"
        />
        <q-input
            filled
            :mask="inputMask"
            :placeholder="field.options?.placeholder || displayFormat"
            :model-value="modelValue"
            :readonly="readOnly"
            :class="field.options?.inline ? 'date-field-input-inline' : ''"
            :hide-bottom-space="field.options?.inline ? true : false"
            @update:model-value="onInput"
            @blur="$emit('blur')"
            :rules="rules"
        >
            <template v-slot:append>
                <q-icon v-if="!readOnly" name="event" class="cursor-pointer">
                    <q-popup-proxy transition-show="scale" transition-hide="scale">
                        <q-date
                            :model-value="modelValue"
                            @update:model-value="onDatePick"
                            :mask="pickerMask"
                            :options="dateOptions"
                        />
                    </q-popup-proxy>
                </q-icon>
                <q-icon v-if="hasTime && !readOnly" name="access_time" class="cursor-pointer">
                    <q-popup-proxy transition-show="scale" transition-hide="scale">
                        <q-time
                            :model-value="modelValue"
                            @update:model-value="onTimePick"
                            :mask="pickerMask"
                            :options="timeHourOptions"
                            :minute-options="minuteOptions"
                            format24h
                        />
                    </q-popup-proxy>
                </q-icon>
            </template>
        </q-input>
    </div>
</template>

<script>
    import FieldHeader from './FieldHeader.vue';

    export default {
        name: 'DateField',
        components: { FieldHeader },
        props: {
            field: {
                type: Object,
                required: true,
            },
            modelValue: {
                type: String,
                default: '',
            },
            readOnly: {
                type: Boolean,
                default: false,
            },
            rules: {
                type: Array,
                default: () => ([]),
            },
            dateOptions: {
                type: Function,
                required: true,
            },
            showHeader: {
                type: Boolean,
                default: true,
            },
        },
        emits: ['update:modelValue', 'blur'],
        computed: {
            hasTime() {
                return this.field.fieldType === 'DATETIME';
            },
            displayFormat() {
                return this.hasTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY';
            },
            // Quasar input mask (# = any digit)
            inputMask() {
                return this.hasTime ? '##/##/#### ##:##' : '##/##/####';
            },
            // Mask shared by q-date / q-time so they edit the right portion of the value
            pickerMask() {
                return this.hasTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY';
            },
            // Minute step for the time picker (defaults to every minute)
            minuteOptions() {
                const step = parseInt(this.field.options?.step, 10);
                if (!Number.isInteger(step) || step <= 1) return null;
                const minutes = [];
                for (let m = 0; m < 60; m += step) minutes.push(m);
                return minutes;
            },
        },
        methods: {
            onInput(value) {
                this.$emit('update:modelValue', value);
            },
            onDatePick(value) {
                this.$emit('update:modelValue', value);
                // For datetime, the time popup is still to come — don't blur/validate yet.
                if (!this.hasTime) this.$emit('blur');
            },
            onTimePick(value) {
                this.$emit('update:modelValue', value);
                this.$emit('blur');
            },
            // Constrain selectable hours from the field's minTime/maxTime (HH:mm) options
            timeHourOptions(hr) {
                const min = this.parseTime(this.field.options?.minTime);
                const max = this.parseTime(this.field.options?.maxTime);
                if (min && hr < min.hour) return false;
                if (max && hr > max.hour) return false;
                return true;
            },
            parseTime(time) {
                if (typeof time !== 'string') return null;
                const m = time.match(/^(\d{1,2}):(\d{2})$/);
                if (!m) return null;
                return { hour: parseInt(m[1], 10), minute: parseInt(m[2], 10) };
            },
        },
    };
</script>

<style scoped>
    .date-field-inline {
        display: flex;
        align-items: center;
    }

    .date-field-header-inline {
        flex: 0 0 auto;
        min-width: 180px;
    }

    .date-field-input-inline {
        flex: 1 1 auto;
    }
</style>
